import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { Bell, Keyboard, Lock, Menu, Plus, Search } from "lucide-react";
import { useHiddenLock, useIdleTimeout } from "../app/idleLock.js";
import { useLockVault } from "../app/lockVault.js";
import { paths } from "../app/paths.js";
import { useSessionKeepAlive } from "../app/keepAlive.js";
import { HIDDEN_LOCK_GRACE_MS, useAutoLockMinutes, useLockOnHide } from "../app/preferences.js";
import { useSession } from "../app/session.js";
import { useAutoEnrollment } from "../app/useAutoEnrollment.js";
import { cappedAutoLock } from "../api/policies.js";
import { useMe } from "../data/account.js";
import { PolicyBanner, policyGate } from "./PolicyBanner.js";
import { Dialog } from "../ui/Dialog.js";
import { Button, IconButton, Kbd } from "../ui/primitives.js";
import { CommandPalette } from "./CommandPalette.js";
import { LockCountdown } from "./LockCountdown.js";
import { ReauthDialog } from "./ReauthDialog.js";
import { StepUpDialog } from "./StepUpDialog.js";
import { ShortcutsDialog } from "./ShortcutsDialog.js";
import { Sidebar, SidebarNav } from "./Sidebar.js";
import { useInviteNotifications } from "./useInviteNotifications.js";
import { modifierLabel, useGlobalShortcuts } from "./shortcuts.js";
import type { ShortcutHandlers } from "./shortcuts.js";

export function AppShell(): ReactNode {
  const { state, passwordLength } = useSession();
  const lock = useLockVault();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const autoLock = cappedAutoLock(useAutoLockMinutes(), me.data?.policies.maxAutoLockMinutes ?? null);
  const gate = policyGate(me.data, passwordLength);
  const lockOnHide = useLockOnHide();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const unlocked = state.status === "unlocked";
  const modifier = modifierLabel();

  const lockIdle = useCallback((): void => void lock("idle"), [lock]);
  const idle = useIdleTimeout(unlocked, autoLock, lockIdle);
  useHiddenLock(unlocked && lockOnHide, HIDDEN_LOCK_GRACE_MS, lockIdle);
  useSessionKeepAlive(unlocked);
  useAutoEnrollment(me.data);
  const invites = useInviteNotifications();

  const shortcuts = useMemo(
    (): ShortcutHandlers => ({
      togglePalette: (): void => setPaletteOpen((open: boolean): boolean => !open),
      openPalette: (): void => setPaletteOpen(true),
      openHelp: (): void => setHelpOpen(true),
      lock: (): void => void lock("manual"),
      navigate: (to: string): void => void navigate(to)
    }),
    [lock, navigate]
  );
  useGlobalShortcuts(shortcuts);

  return (
    <div className="flex min-h-dvh bg-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:rounded-lg focus:bg-flow focus:px-4 focus:py-2 focus:text-ink"
      >
        Zum Inhalt springen
      </a>
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-ink/85 px-3 backdrop-blur-md sm:gap-3 sm:px-6">
          <IconButton label="Navigation öffnen" icon={Menu} className="lg:hidden" onClick={(): void => setMenuOpen(true)} />
          <button
            type="button"
            onClick={(): void => setPaletteOpen(true)}
            aria-label="Suche und Befehle öffnen"
            className="flex h-9 min-w-0 max-w-md flex-1 items-center gap-2.5 rounded-lg border border-line bg-ink-2 px-3 text-left text-sm text-mist-2 transition-colors hover:border-line-strong hover:text-mist"
          >
            <Search size={15} aria-hidden="true" className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">Einträge, Seiten, Befehle …</span>
            <span className="hidden items-center gap-1 sm:flex" aria-hidden="true">
              <Kbd>{modifier}</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <LockCountdown key={`${autoLock}-${String(unlocked)}`} idle={idle} />
            {invites.length > 0 ? (
              <span className="relative inline-flex">
                <IconButton
                  label={invites.length === 1 ? "1 offene Team-Einladung" : `${invites.length} offene Team-Einladungen`}
                  icon={Bell}
                  onClick={(): void => void navigate(paths.teams)}
                />
                <span aria-hidden="true" className="pointer-events-none absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-flow px-1 text-[0.625rem] font-semibold leading-4 text-ink">
                  {invites.length}
                </span>
              </span>
            ) : null}
            <IconButton label="Tastenkürzel (?)" icon={Keyboard} className="hidden sm:inline-flex" onClick={(): void => setHelpOpen(true)} />
            <IconButton label={`Tresor sperren (${modifier}+⇧+L)`} icon={Lock} onClick={(): void => void lock("manual")} />
            <Button icon={Plus} onClick={(): void => void navigate(paths.vaultNew)} className="ml-1">
              <span className="sm:hidden">Neu</span>
              <span className="hidden sm:inline">Neuer Eintrag</span>
            </Button>
          </div>
        </header>

        <main id="main" tabIndex={-1} className="min-w-0 flex-1 outline-none">
          {gate !== undefined ? <PolicyBanner gate={gate} /> : null}
          {gate !== undefined && location.pathname !== paths.settings ? <Navigate to={paths.settings} replace /> : <Outlet />}
        </main>
      </div>

      <Dialog open={menuOpen} onClose={(): void => setMenuOpen(false)} title="Navigation" variant="drawer">
        <div className="h-full">
          <SidebarNav showBrand={false} onNavigate={(): void => setMenuOpen(false)} />
        </div>
      </Dialog>

      <CommandPalette
        open={paletteOpen}
        onClose={(): void => setPaletteOpen(false)}
        onShowShortcuts={(): void => {
          setPaletteOpen(false);
          setHelpOpen(true);
        }}
      />
      <ShortcutsDialog open={helpOpen} onClose={(): void => setHelpOpen(false)} />
      <ReauthDialog />
      <StepUpDialog />
    </div>
  );
}
